import type { ReactNode } from "react";

import {
  Calendar,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Clock,
  Target,
  Users,
} from "lucide-react";

import {
  DH_POLL_CYCLE_CARD,
  DH_POLL_CYCLE_CARD_BODY,
  DH_POLL_CYCLE_CARD_HEADER,
} from "../../Utils/Helper/discoveryHubUi";
import { formatDateUSShort } from "../../Utils/Helper/Helper";
import { MCI_MUTED } from "../../Utils/Helper/sponsorEngagementUi";

type CheckInCycle = {
  id?: string;
  name?: string | null;
  period?: string;
  due_date?: string;
  publication_state?: string;
};

type CheckInPollAccordionProps = {
  pollName: string;
  period: string;
  cycle?: CheckInCycle | null;
  accountCount: number;
  poll1Done?: number;
  poll1Total?: number;
  progressLabel?: string;
  isExpanded: boolean;
  onToggleExpand: () => void;
  emptyHint?: string;
  showPublicationBadge?: boolean;
  children: ReactNode;
};

const formatDueDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return "—";
  try {
    return formatDateUSShort(dateStr, true);
  } catch {
    return dateStr;
  }
};

const pubStateLabel = (state: string | undefined) =>
  state ? String(state).replace(/_/g, " ") : "—";

const MetadataItem = ({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string;
}) => (
  <div className="flex items-center gap-1">
    {icon}
    <span className="text-gray-500">{label}:</span>
    <span className="font-medium text-gray-900">{value}</span>
  </div>
);

const CheckInPollAccordion = ({
  pollName,
  period,
  cycle,
  accountCount,
  poll1Done = 0,
  poll1Total = 0,
  progressLabel = "Poll 1",
  isExpanded,
  onToggleExpand,
  emptyHint = "No accounts in this section.",
  showPublicationBadge = true,
  children,
}: CheckInPollAccordionProps) => {
  const subtitle = period || undefined;

  return (
    <div className={DH_POLL_CYCLE_CARD}>
      <div className={DH_POLL_CYCLE_CARD_HEADER}>
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={onToggleExpand}
            className="inline-flex items-center justify-center p-0 mt-0.5 border-none bg-transparent text-gray-500 cursor-pointer shrink-0 transition-colors hover:text-gray-900"
            aria-expanded={isExpanded}
            aria-label={isExpanded ? "Collapse poll" : "Expand poll"}
          >
            {isExpanded ? (
              <ChevronDown size={20} />
            ) : (
              <ChevronRight size={20} />
            )}
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center flex-wrap gap-2">
              <h3 className="m-0 text-lg font-semibold text-gray-900 leading-snug">
                {pollName}
              </h3>
              {showPublicationBadge && cycle?.publication_state ? (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
                  {pubStateLabel(cycle.publication_state)}
                </span>
              ) : null}
            </div>
            {subtitle && (
              <p className={`m-0 mt-1 text-sm ${MCI_MUTED}`}>{subtitle}</p>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-4 text-sm text-gray-600">
          <MetadataItem
            icon={<Calendar size={16} />}
            label="Period"
            value={period || "—"}
          />
          <MetadataItem
            icon={<Clock size={16} />}
            label="Due"
            value={formatDueDate(cycle?.due_date)}
          />
          <MetadataItem
            icon={<CheckCircle size={16} />}
            label="Phase"
            value="Collection"
          />
          {poll1Total > 0 && (
            <MetadataItem
              icon={<Target size={16} />}
              label={progressLabel}
              value={`${poll1Done} / ${poll1Total}`}
            />
          )}
          <MetadataItem
            icon={<Users size={16} />}
            label="Accounts"
            value={String(accountCount)}
          />
        </div>
      </div>

      {isExpanded && (
        <div className={DH_POLL_CYCLE_CARD_BODY}>
          {accountCount === 0 ? (
            <p className={`m-0 py-6 px-2 text-center text-sm ${MCI_MUTED}`}>
              {emptyHint}
            </p>
          ) : (
            children
          )}
        </div>
      )}
    </div>
  );
};

export default CheckInPollAccordion;
